import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  Button,
  EmptyState,
  FONT_SIZES,
  FONT_WEIGHTS,
  Icon,
  Text,
  WishlistIconName,
  tabletAndUp,
  useExpensiveValueByViewport,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/AuthenticationProvider';
import {useIsAuthenticated} from '../../../features/Authentication/hooks/useIsAuthenticated';
import {useSelectedDiscountCallback} from '../../Campaigns/SelectedDiscounts/useSelectedDiscount';

export const WishlistEmpty: React.FC<{
  isDiscountedItemsTabEmpty?: boolean;
}> = ({isDiscountedItemsTabEmpty}) => {
  const {t} = useTranslation();
  const {isAuthenticated} = useIsAuthenticated();
  const trackFeature = useTrackFeature();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const getSelectedDiscount = useSelectedDiscountCallback();
  const history = useHistory();

  const Asset = useExpensiveValueByViewport<React.ComponentType>(
    {
      small: CompactIcon,
    },
    StyledIcon,
  );

  return (
    <EmptyState
      title={
        <StyledHeading as="h1">
          {!isDiscountedItemsTabEmpty
            ? t('Your wishlist is empty')
            : t('You have no items from this deal in your wishlist')}
        </StyledHeading>
      }
      subtitle={
        !isDiscountedItemsTabEmpty
          ? t(
              'Click on the heart to add an item to your wishlist or log into your account.',
            )
          : ''
      }
      cta={
        <>
          {!isAuthenticated && !isDiscountedItemsTabEmpty && (
            <Button
              type="secondary"
              withBorder
              disableShadow
              data-testid="WishlistLoginButton"
              onClick={() => {
                trackFeature({eventType: 'click', label: 'wishlist_login'});
                showUserAuthenticationPrompt({
                  needsCheckoutSession: false,
                });
              }}
            >
              {t('Login')}
            </Button>
          )}
          {isDiscountedItemsTabEmpty && (
            <Button
              type="secondary"
              withBorder
              disableShadow
              data-testid="WishlistDiscoverNowButton"
              onClick={async () => {
                const selectedDiscount = await getSelectedDiscount();
                if (selectedDiscount && selectedDiscount.linkTarget) {
                  history.push(selectedDiscount.linkTarget.url);
                }
              }}
            >
              {t('Discover now')}
            </Button>
          )}
        </>
      }
      data-testid="WishlistEmpty"
      asset={<Asset />}
    />
  );
};

const CompactIcon = styled(Icon).attrs(() => ({
  asset: WishlistIconName.EmptyCompact,
}))`
  width: 150px;
  height: 200px;
`;

const StyledIcon = styled(Icon).attrs(() => ({asset: WishlistIconName.Empty}))`
  width: 343px;
  height: 224px;
`;

const StyledHeading = styled(Text)`
  font-weight: ${FONT_WEIGHTS.bold};
  font-size: ${FONT_SIZES.px20};

  ${tabletAndUp(css`
    font-size: ${FONT_SIZES.px24};
  `)}
`;
